import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';

import { i18n as I18n } from 'utils/i18n';

import Badge from '../badge';
import useOutsideClick from '../../hooks/useOutsideClick';

const FilterDropdown = ({
  label,
  compressLabel,
  name,
  selectedItems,
  clearAllText,
  children,
  canApply,
  applyOnClickOutside,
  handleApply,
  handleClearAll,
  expandable,
  displayButtons,
  showDropdownArrow,
  alwaysShowBadgeCount,
  positionRelative,
  hideApplyButton,
  testId,
  selectAll,
  allSelected,
  isEmptyFilters,
  hideCancelButton,
  hideSelectAllButton,
  isOpen: isOpenProp = false,
  setIsOpen: setIsOpenProp,
}) => {
  const [internalIsOpen, internalSetIsOpen] = useState(isOpenProp);
  const isOpen = setIsOpenProp ? isOpenProp : internalIsOpen;
  const setIsOpen = setIsOpenProp || internalSetIsOpen;
  const ref = useRef();

  const classes = classNames({
    field: true,
    'field--no-margin': true,
    'filter-dropdown': true,
    'filter-dropdown--open': !!isOpen && !!showDropdownArrow,
    'filter-dropdown--closed': !isOpen && !!showDropdownArrow,
    [`filter-dropdown--${name}`]: name,
  });

  const innerClasses = classNames({
    'filter-dropdown__inner': true,
    'filter-dropdown__inner--expandable': !!expandable,
    'filter-dropdown__inner--relative': !!positionRelative,
  });

  const buttonClasses = classNames({
    button: true,
    'button--block': true,
    'button--dropdown': !!showDropdownArrow,
    'button--dropdown-without-arrow': !showDropdownArrow,
  });

  const items = selectedItems.filter(item => item);

  const renderBadge = () => {
    if ((alwaysShowBadgeCount && items.length >= 1) || items.length > 1) {
      return <Badge count={items.length} />;
    }

    return null;
  };

  const apply = useCallback(() => {
    handleApply();
    setIsOpen(false);
  }, [handleApply, setIsOpen]);

  // Apply filter changes when 'enter' key is pressed
  const handleKeyDown = useCallback(
    event => {
      if (event.keyCode === 13) {
        apply();
      }
    },
    [apply]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, [handleKeyDown]);

  useOutsideClick(ref, () => {
    setIsOpen(false);
  });

  useEffect(() => {
    if (applyOnClickOutside && !isOpen) {
      handleApply();
    }
  }, [applyOnClickOutside, handleApply, isOpen]);

  useEffect(() => {
    if (isOpen && expandable) {
      document.body.style.overflow = 'hidden';
    } else if (!isOpen && expandable) {
      document.body.style.overflow = 'visible';
    }
  });

  const clear = () => {
    handleClearAll();
    setIsOpen(false);
  };

  const handleClick = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  };

  const selectButton = () => {
    if (hideSelectAllButton) {
      return null;
    }
    if (allSelected) {
      return (
        <button
          onClick={handleClearAll}
          className="button button--text right-align"
          data-cy="deselectAllFilters"
          data-testid="deselectAllFilters"
        >
          Deselect All
        </button>
      );
    }
    return (
      <button
        onClick={selectAll}
        className="button button--text right-align"
        data-cy="selectAllFilters"
        data-testid="selectAllFilters"
      >
        Select All
      </button>
    );
  };

  const dropdownLabel = () => {
    const labelLengthLimit = 10;

    if (selectedItems.length === 1) {
      if (compressLabel && selectedItems[0].length > labelLengthLimit) {
        return `${selectedItems[0].slice(0, labelLengthLimit)}...`;
      }
      return selectedItems[0];
    }

    if (compressLabel && label.length > labelLengthLimit) {
      return `${label.slice(0, labelLengthLimit)}...`;
    }

    return label;
  };

  return (
    <div ref={ref} className="form__field" data-testid="filterDropdown">
      <div className={classes}>
        <button
          data-testid={testId}
          className={buttonClasses}
          onClick={handleClick}
          data-cy={testId}
        >
          {dropdownLabel()}
          {renderBadge()}
          {!showDropdownArrow && (
            <ReactSVG
              src="/images/icons/filters.svg"
              id="mobile-search-filters"
            />
          )}
        </button>
        {!!isOpen && (
          <div className={innerClasses} data-testid="filterOptions">
            {!isEmptyFilters && <div className="buttons">{selectButton()}</div>}
            {isEmptyFilters && <p>No options matched.</p>}
            {!isEmptyFilters && children}
            {!!displayButtons && (
              <div className="buttons">
                {!hideCancelButton && (
                  <button
                    onClick={clear}
                    className="button button--text"
                    data-cy="resetAllFilters"
                  >
                    {clearAllText}
                  </button>
                )}
                {!hideApplyButton && !isEmptyFilters && (
                  <button
                    onClick={apply}
                    className="button button--primary button--small right-align"
                    disabled={!canApply}
                    data-cy="apply"
                  >
                    {I18n.t('apply')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterDropdown;

FilterDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  compressLabel: PropTypes.bool,
  selectedItems: PropTypes.arrayOf(PropTypes.string),
  clearAllText: PropTypes.string,
  // showBadge: PropTypes.bool,
  // badgeCount: PropTypes.number,
  alwaysShowBadgeCount: PropTypes.bool,
  children: PropTypes.node,
  canApply: PropTypes.bool,
  applyOnClickOutside: PropTypes.bool,
  handleApply: PropTypes.func,
  handleClearAll: PropTypes.func,
  expandable: PropTypes.bool,
  displayButtons: PropTypes.bool,
  hideApplyButton: PropTypes.bool,
  showDropdownArrow: PropTypes.bool,
  positionRelative: PropTypes.bool,
  testId: PropTypes.string,
  selectAll: PropTypes.func,
  allSelected: PropTypes.bool,
  hideCancelButton: PropTypes.bool,
  hideSelectAllButton: PropTypes.bool,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
};

FilterDropdown.defaultProps = {
  compressLabel: true,
  selectedItems: [],
  // showBadge: false,
  // badgeCount: 0,
  clearAllText: 'Clear',
  alwaysShowBadgeCount: false,
  children: null,
  canApply: true,
  applyOnClickOutside: false,
  handleApply: () => {},
  handleClearAll: () => {},
  expandable: false,
  displayButtons: true,
  hideApplyButton: false,
  showDropdownArrow: true,
  positionRelative: false,
  selectAll: () => {},
  allSelected: false,
  hideCancelButton: false,
  hideSelectAllButton: true,
};
